"use client";

import {
  CopyButton,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { INTRACLASS_CORRELATION_CALCULATOR_STRINGS } from "@/components/tools/intraclass-correlation-calculator";
import {
  DataGrid,
  WorkingLine,
} from "@/components/tools/stats-clinical/data-grid";
import { t } from "@/lib/i18n/interpolate";
import {
  ICC_FORMS,
  type IccBand,
  type IccEstimate,
  type IccForm,
  type IccResult,
  kooLiBand,
} from "@/lib/tool-kit/stats-clinical/icc";
import {
  apaBounded,
  type Formatter,
  fixed,
  formatP,
} from "@/lib/tool-kit/stats-research/format";

/*
 * The result panel of the intraclass correlation calculator: the chosen form, all six
 * forms side by side, the ANOVA table and the working. Strings are the widget's own.
 */

type Strings = typeof INTRACLASS_CORRELATION_CALCULATOR_STRINGS;

const DASH = "—";

const BAND_KEY: Record<IccBand, keyof Strings> = {
  poor: "bandPoor",
  moderate: "bandModerate",
  good: "bandGood",
  excellent: "bandExcellent",
};

const SHROUT_FLEISS_KEY: Record<IccForm, keyof Strings> = {
  "1,1": "name11",
  "2,1": "name21",
  "3,1": "name31",
  "1,k": "name1k",
  "2,k": "name2k",
  "3,k": "name3k",
};

const MCGRAW_WONG_KEY: Record<IccForm, keyof Strings> = {
  "1,1": "mw11",
  "2,1": "mw21",
  "3,1": "mw31",
  "1,k": "mw1k",
  "2,k": "mw2k",
  "3,k": "mw3k",
};

const DESCRIPTION_KEY: Record<IccForm, keyof Strings> = {
  "1,1": "describe11",
  "2,1": "describe21",
  "3,1": "describe31",
  "1,k": "describe1k",
  "2,k": "describe2k",
  "3,k": "describe3k",
};

function bound(fmt: Formatter, value: number): string {
  return Number.isFinite(value) ? fixed(fmt, value, 3) : DASH;
}

function interval(fmt: Formatter, entry: IccEstimate): string {
  return `${bound(fmt, entry.lower)} – ${bound(fmt, entry.upper)}`;
}

function bandText(strings: Strings, value: number): string {
  return Number.isFinite(value) ? strings[BAND_KEY[kooLiBand(value)]] : DASH;
}

function levelLabel(fmt: Formatter, level: number): string {
  return fmt(level * 100, { maximumFractionDigits: 1 });
}

export function formName(strings: Strings, form: IccForm): string {
  return strings[SHROUT_FLEISS_KEY[form]];
}

function apaLine(
  strings: Strings,
  fmt: Formatter,
  entry: IccEstimate,
  level: number
): string {
  const p =
    entry.p < 0.001
      ? `< ${apaBounded(fmt, 0.001, 3)}`
      : `= ${apaBounded(fmt, entry.p, 3)}`;
  return t(strings.report, {
    form: formName(strings, entry.form),
    icc: apaBounded(fmt, entry.estimate, 2),
    level: levelLabel(fmt, level),
    lower: Number.isFinite(entry.lower)
      ? apaBounded(fmt, entry.lower, 2)
      : DASH,
    upper: Number.isFinite(entry.upper)
      ? apaBounded(fmt, entry.upper, 2)
      : DASH,
    df1: fmt(entry.df1),
    df2: fmt(entry.df2),
    f: fixed(fmt, entry.f, 2),
    p,
  });
}

function AllForms({
  result,
  selected,
  strings,
  fmt,
}: {
  result: IccResult;
  selected: IccForm;
  strings: Strings;
  fmt: Formatter;
}) {
  const level = levelLabel(fmt, result.level);
  return (
    <section className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.allFormsHeading}</h3>
      <DataGrid
        head={[
          strings.columnShrout,
          strings.columnMcGraw,
          strings.columnModel,
          strings.columnIcc,
          t(strings.columnInterval, { level }),
          strings.columnBand,
        ]}
        highlight={ICC_FORMS.indexOf(selected)}
        rows={result.estimates.map((entry) => [
          formName(strings, entry.form),
          strings[MCGRAW_WONG_KEY[entry.form]],
          strings[DESCRIPTION_KEY[entry.form]],
          fixed(fmt, entry.estimate, 3),
          interval(fmt, entry),
          bandText(strings, entry.estimate),
        ])}
      />
    </section>
  );
}

function AnovaTable({
  result,
  strings,
  fmt,
}: {
  result: IccResult;
  strings: Strings;
  fmt: Formatter;
}) {
  const { anova } = result;
  const four = (value: number) => fixed(fmt, value, 4);
  const { n, k } = anova;
  return (
    <section className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.anovaHeading}</h3>
      <DataGrid
        head={[
          strings.columnSource,
          strings.columnSs,
          strings.columnDf,
          strings.columnMs,
        ]}
        rows={[
          [
            strings.sourceSubjects,
            four(anova.ssRows),
            fmt(n - 1),
            four(anova.msRows),
          ],
          [
            strings.sourceRaters,
            four(anova.ssColumns),
            fmt(k - 1),
            four(anova.msColumns),
          ],
          [
            strings.sourceError,
            four(anova.ssError),
            fmt((n - 1) * (k - 1)),
            four(anova.msError),
          ],
          [
            strings.sourceWithin,
            four(anova.ssWithin),
            fmt(n * (k - 1)),
            four(anova.msWithin),
          ],
        ]}
      />
    </section>
  );
}

function Working({
  entry,
  result,
  strings,
  fmt,
}: {
  entry: IccEstimate;
  result: IccResult;
  strings: Strings;
  fmt: Formatter;
}) {
  const { anova } = result;
  const values = {
    msr: fixed(fmt, anova.msRows, 4),
    msc: fixed(fmt, anova.msColumns, 4),
    mse: fixed(fmt, anova.msError, 4),
    msw: fixed(fmt, anova.msWithin, 4),
    n: fmt(anova.n),
    k: fmt(anova.k),
    km1: fmt(anova.k - 1),
    icc: fixed(fmt, entry.estimate, 3),
  };
  const formulaKey: Record<IccForm, keyof Strings> = {
    "1,1": "formula11",
    "2,1": "formula21",
    "3,1": "formula31",
    "1,k": "formula1k",
    "2,k": "formula2k",
    "3,k": "formula3k",
  };
  return (
    <div className={`${CARD} space-y-2`}>
      <h3 className={WIDGET_H3}>{strings.howCalculated}</h3>
      <WorkingLine>{t(strings[formulaKey[entry.form]], values)}</WorkingLine>
      <WorkingLine>
        {t(strings.workingF, {
          f: fixed(fmt, entry.f, 3),
          df1: fmt(entry.df1),
          df2: fmt(entry.df2),
          p: formatP(fmt, entry.p),
        })}
      </WorkingLine>
      <p className="text-muted-foreground text-sm">
        {strings.workingIntervals}
      </p>
    </div>
  );
}

export function IccSummary({
  result,
  selected,
  strings,
  fmt,
}: {
  result: IccResult;
  selected: IccForm;
  strings: Strings;
  fmt: Formatter;
}) {
  const entry = result.estimates.find(
    (candidate) => candidate.form === selected
  );
  if (!entry) {
    return null;
  }
  const level = levelLabel(fmt, result.level);
  const report = apaLine(strings, fmt, entry, result.level);
  return (
    <div aria-live="polite" className="space-y-6">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={formName(strings, entry.form)}
          value={fixed(fmt, entry.estimate, 3)}
        />
        <StatCard
          label={t(strings.columnInterval, { level })}
          value={interval(fmt, entry)}
        />
        <StatCard
          label={t(strings.fTest, { df1: fmt(entry.df1), df2: fmt(entry.df2) })}
          value={`${fixed(fmt, entry.f, 2)}, p ${formatP(fmt, entry.p)}`}
        />
      </StatGrid>
      <p className="text-foreground text-sm leading-relaxed">
        {t(strings.interpretation, {
          icc: fixed(fmt, entry.estimate, 3),
          band: bandText(strings, entry.estimate),
          lowerBand: bandText(strings, entry.lower),
          upperBand: bandText(strings, entry.upper),
        })}{" "}
        <span className="text-muted-foreground">
          {strings.interpretationNote}
        </span>
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyResult}
          text={report}
        />
        <span className="font-mono text-muted-foreground text-xs">
          {report}
        </span>
      </div>
      <AllForms
        fmt={fmt}
        result={result}
        selected={selected}
        strings={strings}
      />
      <AnovaTable fmt={fmt} result={result} strings={strings} />
      <Working entry={entry} fmt={fmt} result={result} strings={strings} />
    </div>
  );
}
